import { createSelector } from '@reduxjs/toolkit';
import {
  selectAllPoints,
  selectInterconnects,
  selectInterconnectsAtPoints,
  selectPriceComponents,
} from '../map/slice/mapSelectors';
import { differenceInDays } from '../utils/dateUtils';
import {
  FLOW_ACTIVITY_THRESHOLD,
  NOTICE_ACTIVITY_THRESHOLD,
} from './advancedFiltersSlice';
import Fuse from 'fuse.js';

/**
 *
 * @param {{advancedFilters: import("./advancedFiltersSlice").AdvancedFiltersState}} state
 * @returns
 */
export const selectAdvancedFiltersState = (state) =>
  state.advancedFilters ?? {};

export const selectAdvancedPointFilters = createSelector(
  selectAdvancedFiltersState,
  (filters) => {
    return filters.points ?? {};
  },
);

export const selectPossiblePointTypes = createSelector(
  selectAllPoints,
  (points) => {
    const pointTypes = new Set();
    Object.values(points).forEach((point) => {
      if (point.loc_type_ind) {
        pointTypes.add(point.loc_type_ind.trim().toUpperCase());
      }
    });
    return Array.from(pointTypes);
  },
);

export const selectFilteredPoints = createSelector(
  selectAllPoints,
  selectInterconnectsAtPoints,
  selectAdvancedPointFilters,
  (points, interconnectsAtPoints, pointFilters) => {
    const {
      mapped,
      hasCapacityComponent,
      hasNoticeComponent,
      hasInterconnects,
      textSearch,
      tsps,
    } = pointFilters;

    return Object.values(points)
      .filter((point) => {
        if (
          mapped === true &&
          (point.node === null || point.node === undefined)
        ) {
          return false;
        }
        if (
          mapped === false &&
          point.node !== null &&
          point.node !== undefined
        ) {
          return false;
        }
        if (
          hasCapacityComponent === true &&
          point.capacity_components?.length <= 0
        ) {
          return false;
        }
        if (
          hasCapacityComponent === false &&
          point.capacity_components?.length > 0
        ) {
          return false;
        }
        if (
          pointFilters.hasFlowActivity === true &&
          differenceInDays(new Date(point.flow_activity), new Date()) >
            FLOW_ACTIVITY_THRESHOLD
        ) {
          return false;
        }
        if (
          pointFilters.hasFlowActivity === false &&
          differenceInDays(new Date(point.flow_activity), new Date()) <=
            FLOW_ACTIVITY_THRESHOLD
        ) {
          return false;
        }
        if (
          hasNoticeComponent === true &&
          point.notice_components?.length <= 0
        ) {
          return false;
        }
        if (
          hasNoticeComponent === false &&
          point.notice_components?.length > 0
        ) {
          return false;
        }
        if (
          pointFilters.hasNoticeActivity === true &&
          differenceInDays(new Date(point.notice_activity), new Date()) >
            NOTICE_ACTIVITY_THRESHOLD
        ) {
          return false;
        }
        if (
          pointFilters.hasNoticeActivity === false &&
          differenceInDays(new Date(point.notice_activity), new Date()) <=
            NOTICE_ACTIVITY_THRESHOLD
        ) {
          return false;
        }
        if (
          hasInterconnects === true &&
          !interconnectsAtPoints[point.id]?.length > 0
        ) {
          return false;
        }
        if (
          hasInterconnects === false &&
          interconnectsAtPoints[point.id]?.length > 0
        ) {
          return false;
        }
        if (
          textSearch?.length > 0 &&
          !point.name?.toLowerCase().includes(textSearch.toLowerCase()) &&
          !point.loc_id?.toLowerCase().includes(textSearch.toLowerCase()) &&
          !String(point.id).includes(textSearch)
        ) {
          return false;
        }
        if (tsps?.length > 0 && !tsps.includes(point.tsp_id)) {
          return false;
        }
        if (
          pointFilters.loc_types?.length > 0 &&
          !pointFilters.loc_types.includes(
            point.loc_type_ind?.trim().toUpperCase(),
          )
        ) {
          return false;
        }
        return true;
      })
      .sort((pointA, pointB) => pointA.name?.localeCompare(pointB.name));
  },
);

export const selectPathComponents = createSelector(
  selectAllPoints,
  selectInterconnects,
  selectPriceComponents,
  /**
   *
   * @returns {import('@/types').PathComponent[]}
   */
  (points, interconnects, priceComponents) => {
    /** @type {import('@/types').PathComponent[]} */
    const pointComponents = Object.values(points)
      .filter((point) => Boolean(point.node))
      .map((point) => {
        return {
          type: 'point',
          id: point.id.toString(),
          name: point.name,
          points: [point],
          instance: point,
          sorting_key: point.name.toLocaleLowerCase(),
          search_fields: [point.name, point.loc_id, point.tsp_name],
          attributes: {},
        };
      });

    /** @type {import('@/types').PathComponent[]} */
    const interconnectComponents = Object.values(interconnects)
      .map((interconnect) => {
        const startPoint = points[interconnect.start_point];
        const endPoint = points[interconnect.end_point];
        return [interconnect, startPoint, endPoint];
      })
      .filter(([_, startPoint, endPoint]) => {
        return Boolean(startPoint) && Boolean(endPoint);
      })
      .map(([interconnect, _startPoint, _endPoint]) => {
        let startPoint = _startPoint;
        let canFlowStartToEnd =
          interconnect.allows_flow_start_to_end;
        let endPoint = _endPoint;
        let canFlowEndToStart =
          interconnect.allows_flow_end_to_start;
        if (
          startPoint.flow_direction === 'RECEIPT' ||
          endPoint.flow_direction === 'DELIVERY'
        ) {
          [startPoint, endPoint] = [endPoint, startPoint];
          [canFlowStartToEnd, canFlowEndToStart] = [
            canFlowEndToStart,
            canFlowStartToEnd,
          ];
        }
        const isReverseDisplacement =
          startPoint.flow_direction === 'DELIVERY' || endPoint.flow_direction === 'RECEIPT';
        
        return [
          interconnect,
          startPoint,
          endPoint,
          canFlowEndToStart,
          isReverseDisplacement,
        ];
      })
      .flatMap(
        ([
          interconnect,
          startPoint,
          endPoint,
          canFlowEndToStart,
          isReverseDisplacement,
        ]) => {
          const components = [
            // Normal flow
            {
              type: 'interconnect',
              id: interconnect.id.toString(),
              name: `From ${startPoint.name} to ${endPoint.name}`,
              points: [startPoint, endPoint],
              instance: interconnect,
              sorting_key: startPoint.name.toLocaleLowerCase(),
              search_fields: [
                interconnect.alias,
                startPoint.name,
                startPoint.loc_id,
                startPoint.tsp_name,
                endPoint.name,
                endPoint.tsp_name,
                endPoint.loc_id,
              ],
              attributes: { displacement: false },
            },
            // Reversed flow
            {
              type: 'interconnect',
              disabled: !canFlowEndToStart,
              id: `${interconnect.id}-displacement`,
              name: `From ${endPoint.name} to ${startPoint.name}`,
              points: [endPoint, startPoint],
              instance: interconnect,
              sorting_key: endPoint.name.toLocaleLowerCase(),
              search_fields: [
                interconnect.alias,
                startPoint.name,
                startPoint.loc_id,
                startPoint.tsp_name,
                endPoint.name,
                endPoint.tsp_name,
                endPoint.loc_id,
              ],
              attributes: { displacement: isReverseDisplacement },
            },
          ];
          return components;
        },
      );

    /** @type {import('@/types').PathComponent[]} */
    const poolComponents = Object.values(priceComponents).flatMap(
      (component) => {
        const componentPoints = component.points
          .map((pointId) => points[pointId])
          .filter(Boolean);

        return componentPoints.map((point) => ({
          type: 'pool',
          id: `pool-${component.id}-point-${point.id}`,
          name: component.description,
          points: [point],
          instance: component,
          sorting_key: component.description.toLocaleLowerCase(),
          search_fields: [
            component.description,
            ...componentPoints.flatMap((point) => [
              point.name,
              point.loc_id,
              point.tsp_name,
            ]),
          ],
          attributes: {
            point: point,
          },
        }));
      },
    );

    const sortFunction = (a, b) => a.sorting_key.localeCompare(b.sorting_key);

    return [
      ...poolComponents.sort(sortFunction),
      ...interconnectComponents.sort(sortFunction),
      ...pointComponents.sort(sortFunction),
    ];
  },
);

export const selectFilteredPathComponents = createSelector(
  selectPathComponents,
  selectAdvancedFiltersState,
  /**
   *
   * @param {import('@/types').PathComponent[]} components
   * @param {import('./advancedFiltersSlice').AdvancedFiltersState} advancedFilters
   * @returns {import('@/types').PathComponent[]}
   */
  (components, advancedFilters) => {
    const preFilteredComponents =
      components?.filter((component) => {
        if (advancedFilters.pathComponents.tsps.length > 0) {
          const componentTspIds = new Set(
            component.type === 'interconnect'
              ? [component.points[0].tsp_id]
              : component.points.map((point) => point.tsp_id),
          );
          const hasTsp = advancedFilters.pathComponents.tsps.some((tspId) =>
            componentTspIds.has(tspId),
          );
          if (!hasTsp) {
            return false;
          }
        }

        if (advancedFilters.pathComponents.types.length > 0) {
          if (!advancedFilters.pathComponents.types.includes(component.type)) {
            return false;
          }
        }

        return true;
      }) ?? [];
    const textSearch = advancedFilters.pathComponents.textSearch || '';

    if (textSearch.length === 0) {
      return preFilteredComponents;
    }

    const fuse = new Fuse(preFilteredComponents, {
      keys: ['search_fields'],
      threshold: 0.35, // higher = more fuzzy, lower = more exact
      isCaseSensitive: false,
      ignoreDiacritics: true,
      ignoreLocation: true,
    });

    const results = fuse.search(textSearch);
    const typeOrder = { pool: 0, interconnect: 1, point: 2 };
    return results
      .sort((a, b) => {
        return typeOrder[a.item.type] - typeOrder[b.item.type];
      })
      .map((result) => result.item);
  },
);

export const selectAdvancedCapacityComponentFilters = createSelector(
  selectAdvancedFiltersState,
  (filters) => {
    return filters.capacityComponents ?? {};
  },
);

export const selectAdvancedNoticeComponentFilters = createSelector(
  selectAdvancedFiltersState,
  (filters) => {
    return filters.noticeComponents ?? {};
  },
);

export const selectAdvancedPriceComponentFilters = createSelector(
  selectAdvancedFiltersState,
  (filters) => {
    return filters.priceComponents ?? {};
  },
);

export const selectAdvancedPathComponentFilters = createSelector(
  selectAdvancedFiltersState,
  (filters) => {
    return filters.pathComponents ?? {};
  },
);
